// src/components/crm/QuoteCloseStrip.js
// SEAM 9 VERIFIER: qclose-3e8a5c17-strip
//
// The estimate page's close summary, and the "Why did this close?" nudge.
//
// WHY A NUDGE AND NOT ONLY THE MODAL: the census (2026-09-30) found 93 of 94
// real closes happen by STATUS — the customer approving or declining in the
// portal, a job conversion, a phone status chip — not by anyone dragging a
// card. Nobody is at a keyboard at that moment to be asked, so the question
// waits here, on the estimate, until it is answered or someone presses Skip.
// Skip is stored server-side (recordQuoteCloseDetails { dismiss: true }), so it
// stays dismissed for every staff member and every browser.
//
// Three states, from the live quoteCloses record (useQuoteClose):
//   needs an answer  → amber nudge: [Add reason] [Skip]
//   answered         → one grey line with the answers, [Edit]
//   skipped, empty   → one grey line "no reason recorded", [Add one]
// Nothing at all when the estimate has no live close record (never closed
// under Seam 9, or reopened since).
//
// Answering is gated like the server (PIPELINE_MOVE_ROLES ≙ MANAGE_FINANCIALS,
// the same pairing the follow-up strip relies on). Others see the summary only.
// INTERNAL ONLY: never mount in a portal view.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { Flag } from 'lucide-react';
import { functions } from '../../firebase/config';
import { closeNeedsAnswer } from '../../hooks/useQuoteClose';
import { OUTCOME_WORD } from './CloseDetailsModal';

const QuoteCloseStrip = ({ quote, record, canAnswer, onAnswer, docWord = 'Estimate' }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    if (!quote?.id || !record || record.reopenedAt) return null;

    const word = OUTCOME_WORD[record.outcome] || 'closed';
    const answers = [record.reason, record.priceFeedback].filter(Boolean);

    const skip = async () => {
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            await httpsCallable(functions, 'recordQuoteCloseDetails')({
                quoteId: quote.id, closeActivityId: record.closeActivityId || null, dismiss: true,
            });
        } catch (err) {
            console.error('QuoteCloseStrip: skip failed', err);
            setError(err?.message || 'Could not skip. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (closeNeedsAnswer(record)) {
        if (!canAnswer) return null;
        return (
            <div className="max-w-4xl mx-auto mb-4 px-4 py-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span className="flex items-center gap-2 text-sm font-semibold text-amber-900">
                        <Flag size={16} /> Why was this {docWord.toLowerCase()} {word}?
                    </span>
                    <span className="text-xs text-amber-800">
                        One question — it is what makes the win/loss numbers mean something.
                    </span>
                    <div className="ml-auto flex gap-2">
                        <button type="button" onClick={skip} disabled={busy}
                            title="Don't ask about this one again (for anyone)"
                            className="px-3 py-1.5 text-sm font-medium text-amber-900 bg-white border border-amber-300 rounded-lg hover:bg-amber-100 disabled:opacity-50">
                            {busy ? 'Skipping…' : 'Skip'}
                        </button>
                        <button type="button" onClick={onAnswer} disabled={busy}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8] disabled:opacity-50">
                            Add reason
                        </button>
                    </div>
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </div>
        );
    }

    const empty = !answers.length && !record.note;
    return (
        <div className="max-w-4xl mx-auto mb-4 px-4 py-3 bg-white rounded-lg shadow-sm border border-gray-100">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <Flag size={16} /> Closed as {word}
                </span>
                <span className={`text-sm ${empty ? 'italic text-gray-400' : 'text-gray-800'}`}>
                    {empty ? 'No reason recorded' : answers.join(' · ')}
                </span>
                {canAnswer && (
                    <button type="button" onClick={onAnswer}
                        className="ml-auto text-xs font-semibold text-[#5F4EC9] hover:underline">
                        {empty ? 'Add one' : 'Edit'}
                    </button>
                )}
            </div>
            {record.note && <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap break-words">{record.note}</p>}
            <p className="mt-1 text-xs text-gray-400">Internal: customers never see this.</p>
        </div>
    );
};

export default QuoteCloseStrip;
